/**
 * Receitas x despesas por dia de competência.
 */
import { useMemo, useState } from 'react'
import { formatCurrency } from '../../../../utils/obraAnalytics'

const MovimentacaoFluxoDiario = ({ title = 'Fluxo dia a dia', serie = [], refDia = null }) => {
    const [hover, setHover] = useState(null)

    const dias = useMemo(() => (serie || []).map((r) => {
        const receitas = Number(r.receitas || 0)
        const despesas = Number(r.despesas || 0)
        const impostos = Number(r.impostos || 0)
        const saidas = despesas + impostos
        return {
            dia: r.dia,
            label: r.label ?? r.dia?.slice(8),
            receitas,
            despesas,
            impostos,
            saidas,
            saldo_dia: Number(r.saldo_dia ?? (receitas - despesas - impostos)),
            quantidade: Number(r.quantidade || 0),
            futuro: Boolean(r.futuro),
        }
    }), [serie])

    const max = Math.max(...dias.flatMap((d) => [d.receitas, d.saidas]), 1)
    const temMovimento = dias.some((d) => d.receitas > 0 || d.saidas > 0)
    const active = hover != null ? dias[hover] : null

    if (!dias.length) {
        return (
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                <p className="text-xs text-gray-400 mt-1">Por data de competência</p>
                <div className="flex items-center justify-center h-44 mt-4">
                    <p className="text-xs text-gray-400">Sem dias no período.</p>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-400 mt-1">
                        Receitas e despesas por data de competência — referência: {refDia ? refDia.split('-').reverse().join('/') : 'fim do período'}
                    </p>
                </div>
                {active && (
                    <div className="text-right text-xs space-y-0.5">
                        <p className="font-bold text-gray-800">{active.label}</p>
                        <p className="text-emerald-700 font-semibold">+ {formatCurrency(active.receitas)}</p>
                        <p className="text-rose-700 font-semibold">− {formatCurrency(active.despesas)}</p>
                        {active.impostos > 0 && (
                            <p className="text-amber-700 font-semibold">− {formatCurrency(active.impostos)} impostos</p>
                        )}
                        <p className={`font-bold ${active.saldo_dia >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                            = {formatCurrency(active.saldo_dia)}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-4 text-[11px] font-semibold mb-3">
                <span className="flex items-center gap-1.5 text-emerald-700">
                    <span className="w-3 h-3 rounded-sm bg-emerald-500" /> Receitas
                </span>
                <span className="flex items-center gap-1.5 text-rose-700">
                    <span className="w-3 h-3 rounded-sm bg-rose-500" /> Despesas + Impostos
                </span>
                {refDia && (
                    <span className="flex items-center gap-1.5 text-amber-700">
                        <span className="w-3 h-0.5 bg-amber-400 border-dashed" /> Data ref.
                    </span>
                )}
            </div>

            {!temMovimento ? (
                <div className="flex items-center justify-center h-44">
                    <p className="text-xs text-gray-400">Nenhum lançamento no período.</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <div
                        className="flex items-end gap-0.5 h-48 px-1 relative"
                        style={{ minWidth: `${Math.max(dias.length * 18, 280)}px` }}
                    >
                        {dias.map((d, idx) => {
                            const hRec = d.receitas > 0 ? Math.max(6, Math.round((d.receitas / max) * 100)) : 0
                            const hDesp = d.saidas > 0 ? Math.max(6, Math.round((d.saidas / max) * 100)) : 0
                            const ativo = hover === idx
                            const isRef = refDia && d.dia === refDia
                            return (
                                <div
                                    key={`${d.dia}-${idx}`}
                                    className={`flex flex-col items-center justify-end h-full flex-1 min-w-[14px] max-w-[28px] ${isRef ? 'bg-amber-50/80 rounded-t' : ''}`}
                                    onMouseEnter={() => setHover(idx)}
                                    onMouseLeave={() => setHover(null)}
                                >
                                    <div className="flex items-end gap-px w-full h-[85%]">
                                        <div
                                            className={`flex-1 rounded-t-sm transition-all ${d.receitas > 0 ? 'bg-emerald-500' : 'bg-transparent'} ${ativo ? 'ring-1 ring-emerald-300' : ''}`}
                                            style={{ height: hRec ? `${hRec}%` : '0' }}
                                        />
                                        <div
                                            className={`flex-1 rounded-t-sm transition-all ${d.saidas > 0 ? 'bg-rose-500' : 'bg-transparent'} ${ativo ? 'ring-1 ring-rose-300' : ''}`}
                                            style={{ height: hDesp ? `${hDesp}%` : '0' }}
                                        />
                                    </div>
                                    <span className={`text-[8px] mt-1 leading-tight text-center w-full truncate ${ativo ? 'text-gray-800 font-bold' : 'text-gray-400'}`}>
                                        {d.label?.slice(0, 5)}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default MovimentacaoFluxoDiario
